/***
 * Created on 2025/5/7
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useReducer} from 'react';
import {useTranslation} from "react-i18next";
import ic_sys_info_dark from '../../static/img/utils/ic-sys-info--dark.svg';
import TableComponent from "../common/TableComponent/TableComponent";
import CloudSpaceLimitStorageUserModal from "./CloudSpaceLimitStorageUserModal";
import {dataCellLabelToDataGridArea, normalizeSpaceSize} from "../../service/utils";
import UserAvatarIcon from "../common/UserAvatarIcon";
import ic_remove from '../../static/img/utils/ic-remove.svg';
import CloudSpaceRemoveLimitUserWarningDialog from "./CloudSpaceRemoveLimitUserWarningDialog";
import {useQuery} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import UserStatusComponent from "../common/UserStatusComponent";

function reducerHandler(state, action) {
    switch (action.type) {
        case 'select_all':
            const selected = state.data.map((item) => {item.checked = true; return item});
            return {
                ...state,
                data: selected,
                select_box: true
            }
        case 'select_none':
            const unSelected = state?.data?.map((item) => {item.checked = false; return item});
            return {
                ...state,
                data: unSelected,
                select_box: false
            }
        case 'toggle_checkbox':
            const updatedData = state.data.map((item) => {
                if (item.user_id === action.payload.user_id) {
                    return {...item, checked: !item.checked};
                }
                return item;
            });
            let check_status = updatedData?.every((o) => o.checked);
            if (!check_status) {
                check_status = updatedData?.some((o) => o.checked) ? 'partial' : false;
            }
            return {
                ...state,
                data: updatedData,
                select_box: check_status
            }
        case 'toggle_warning':
            return {
                ...state,
                remove_unrestricted_user: action.payload.remove_unrestricted_user,
                show_warning: action.payload.show_warning
            }
        case 'set_data':
            return {
                ...state,
                data: action.payload.data
            }
        default:
            return state;
    }
}

function CloudSpaceLimitEmptyList({t}) {
    return (
        <div className="flexbox-tbody">
            <div className="no-data-screen">
                <div className="no-data-info">
                    <div className="no-data-desc">
                        <div className="desc-title">{t('@No members added yet@')}</div>
                        <div className="desc-msg">{t('@You can designate members who will have unrestricted storage space through the Add button above.@')}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CloudSpaceLimitStorageUserRow({row_data, index, show_checkbox=false, disPatcher, table_head_fields, translate:t, isOwner}) {
    const toggle_checkbox = () => {
        disPatcher({type:'toggle_checkbox', payload: row_data});
    }

    const usage = row_data?.quota_usages?.filter((o) => o.space_kind === 'org_personal')?.reduce((total, u) => total + u.usage, 0) || null;

    return (
            <div className="flexbox-trow" key={index}>
                <div className="tbody-cell select-cell">
                    <div className={`comp-checkbox no-label ${show_checkbox ? 'visible-checkbox' : ''}`}>
                        <input type="checkbox" id={`cb-${index}`}
                               checked={row_data.checked || false}
                               onChange={() => toggle_checkbox(row_data)}
                        />
                        <label htmlFor={`cb-${index}`} className="comp-label"/>
                    </div>
                </div>
                <div className={`tbody-cell ${table_head_fields[0].width_style}`} data-cell-label={t(table_head_fields[0].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                    <div className="account-info-group">
                        <UserAvatarIcon user_info={row_data} with_user_info={true} avatar_class='admin'/>
                    </div>
                </div>
                <div className={`tbody-cell ${table_head_fields[1].width_style}`} data-cell-label={t(table_head_fields[1].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                    {!usage ?  (row_data?.seated_regions ? normalizeSpaceSize(0) : '-') : normalizeSpaceSize(usage)}
                </div>
                <div className={`tbody-cell ${table_head_fields[2].width_style}`} data-cell-label={t(table_head_fields[2].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                    {t('@Unrestricted@')}
                </div>
                <div className={`tbody-cell ${table_head_fields[3].width_style}`} data-cell-label={t(table_head_fields[3].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[3].field_name)}>
                    <UserStatusComponent user={row_data}/>
                </div>
                <div className={`tbody-cell ${table_head_fields[4].width_style}`} data-cell-label={t(table_head_fields[4].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[4].field_name)}>
                    {isOwner &&
                        <div className="act-btn-group" onClick={() => disPatcher({type: 'toggle_warning', payload: {show_warning: true, remove_unrestricted_user: [row_data]}})}>
                            <div className="act-btn">
                                <img src={ic_remove} alt=""/>
                            </div>
                        </div>
                    }
                </div>
        </div>
    );
}

function CloudSpaceLimitUserList({usage_data, isReadOnly = false}) {
    const {t} = useTranslation();
    const [showAddUserDialog, setShowAddUserDialog] = React.useState(false);
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const org = getCurrentOrg();

    const table_head_fields = [
        {field_name: t('@Member@'), isSortable: true, width_style: '!w-28', field_id: 'display_name', default_sort: true, default_sort_order: ''},
        {field_name: t('@Personal Space Usage@'), isSortable: false, width_style: '!w-22', info:{show_info: true, info_text: t('@Storage used in the Personal Space of this user only. Shared Space usage is not included.@')}},
        {field_name: t('@User Space Limit@'), isSortable: false, width_style: '!w-20'},
        {field_name: t('@Status@'), isSortable: false, width_style: '!w-20'},
        {field_name: t('@Action@'), isSortable: false, width_style: '!w-10'},
    ];

    const {data, isFetched} = useQuery({
        queryKey: ['unrestricted_storage_users_with_seated', usage_data?.exception_user_ids],
        queryFn: async (data) => {
            const resp = await OrganizationAPI.q2.get(`org_storage/${org.organization_id}/user?seated=true`)
            const exception_user_ids = usage_data?.exception_user_ids?.map((item) => item.id);
            const orgUsers = org?.users || [];
            const result = resp.result.filter(item=>orgUsers.find((u) => u.id === item.user_id)).map((item) => {
                const user = orgUsers.find((u) => u.id === item.user_id);
                return user ?{
                    ...item,
                    status: user.status,
                    id: item?.user_id,
                } : null
            }).filter(item => exception_user_ids?.includes(item.user_id));
            return result;
        },
    });

    const addUserDialog = () => {
        setShowAddUserDialog(true);
    }

    const batchRemoveUnrestrictedUser = () => {
        const selectedUsers = state.data.filter((item) => item.checked);
        if (selectedUsers.length > 0) {
            dispatch({type: 'toggle_warning', payload: {show_warning: true, remove_unrestricted_user: selectedUsers}});
        }
    }

    const reducerInitialize = () => {
        return {
            select_box: false,
            show_warning: false,
            data: data || [],
            usage_data
        }
    }

    const [state, dispatch] = useReducer(reducerHandler, {}, reducerInitialize);

    useEffect(() => {
        dispatch({type: 'set_data', payload: {data: data}});
    }, [isFetched, data]);

    return (
        <div>
            <div className="section-content__divide"></div>
            <div className="toolbar" style={{zIndex: 2}}>
                <div className="col-wrapper">
                    <div className="col-left">
                        <div className="amount-info fs14">
                            {t('@Unrestricted storage users:@')}
                            <span className="hi-light !pl-8">
                                {usage_data?.exception_user_ids?.length || 0}
                            </span>
                            <span className="ic-info-dark tooltip">
                                <img src={ic_sys_info_dark} alt=""/>
                                <span className="tooltiptext">{t('@You can designate members who will have unrestricted storage space.@')}</span>
                            </span>
                        </div>
                        <div className="func-divide !ml-16 !mr-16"></div>
                        {!isReadOnly &&
                            <div className="q-btn-group">
                                <button type="button" className="q-btn q-btn-primary q-btn-small q-btn-round-corner" onClick={addUserDialog}>{t('@Add user@')}</button>
                                <button type="button" className="q-btn q-btn-default q-btn-small q-btn-round-corner"
                                        onClick={batchRemoveUnrestrictedUser}
                                        disabled={!state.select_box}>{t('@Remove@')}</button>
                            </div>
                        }
                    </div>
                </div>
            </div>

            <TableComponent
                row_component={CloudSpaceLimitStorageUserRow}
                table_head_fields={table_head_fields}
                table_data={state}
                total_record={0}
                doRefresh={() => {}}
                disPatcher={dispatch}
                table_ui={{table_field_checkbox: true}}
                search={false}
                isOwner={!isReadOnly}
                createText={null}
                permissions={null}
                empty_data={<CloudSpaceLimitEmptyList t={t}/>}
                data_table_name="unrestricted-storage-users-table"
                css_with_context_wrapper={false}
                pagination={false}
                table_height="100%"
            />

            {showAddUserDialog && <CloudSpaceLimitStorageUserModal isOpen={showAddUserDialog} onClose={()=>setShowAddUserDialog(false)} unrestricted_users={data || []} usage_data={usage_data} />}
            {state?.show_warning && <CloudSpaceRemoveLimitUserWarningDialog isOpen={state?.show_warning}  onClose={()=>dispatch({type: 'toggle_warning', payload: {show_warning: false}})} state={state} dispatch={dispatch}/>}
        </div>
    );
}

export default CloudSpaceLimitUserList;